/**
 * Route Configuration
 * Centralized route constants for the application
 */

export const ROUTES = {
  // Public routes
  HOME: '/',
  
  // Auth routes
  AUTH: {
    LOGIN: '/login',
    REGISTER: '/register',
    FORGOT_PASSWORD: '/forgot-password',
    RESET_PASSWORD: '/reset-password',
    VERIFY_EMAIL: '/verify-email',
  },
  
  // Student routes
  STUDENT: {
    DASHBOARD: '/student/dashboard',
    EXAMS: '/student/exams',
    EXAM_DETAIL: '/student/exams/:examId',
    TAKE_EXAM: '/student/exams/:examId/take',
    RESULTS: '/student/results',
    RESULT_DETAIL: '/student/results/:attemptId',
    PROGRESS: '/student/progress',
    PROFILE: '/student/profile',
    SETTINGS: '/student/settings',
    APPSOKAY_TUTOR: '/student/tutor',
  },
  
  // Teacher routes
  TEACHER: {
    DASHBOARD: '/teacher/dashboard',
    EXAMS: '/teacher/exams',
    EXAM_CREATE: '/teacher/exams/create',
    EXAM_EDIT: '/teacher/exams/:examId/edit',
    EXAM_DETAIL: '/teacher/exams/:examId',
    EXAM_ANALYTICS: '/teacher/exams/:examId/analytics',
    QUESTIONS: '/teacher/questions',
    QUESTION_CREATE: '/teacher/questions/create',
    QUESTION_EDIT: '/teacher/questions/:questionId/edit',
    CLASSES: '/teacher/classes',
    CLASS_DETAIL: '/teacher/classes/:classId',
    GRADING: '/teacher/grading',
    ANALYTICS: '/teacher/analytics',
    PROFILE: '/teacher/profile',
    SETTINGS: '/teacher/settings',
  },
  
  // Admin routes
  ADMIN: {
    DASHBOARD: '/admin/dashboard',
    USERS: '/admin/users',
    USER_DETAIL: '/admin/users/:userId',
    EXAMS: '/admin/exams',
    ORGANIZATIONS: '/admin/organizations',
    ORGANIZATION_DETAIL: '/admin/organizations/:orgId',
    SCHOOLS: '/admin/schools',
    SCHOOL_DETAIL: '/admin/schools/:tenantId',
    SCHOOL_EDIT: '/admin/schools/:tenantId/edit',
    SCHOOL_ANALYTICS: '/admin/schools/:tenantId/analytics',
    SAAS_DATA: '/admin/saas-data',
    STUDENT_VIEW: '/admin/students/:studentId',
    TEACHER_VIEW: '/admin/teachers/:teacherId',
    SECURITY: '/admin/security',
    SETTINGS: '/admin/settings',
    REPORTS: '/admin/reports',
    AUDIT_LOG: '/admin/audit-log',
    CORS_SUPABASE_TESTING: '/admin/cors-supabase-testing',
    EXAM_CORS_TESTING: '/admin/exam-cors-testing',
  },
  
  // Staff routes
  STAFF: {
    DASHBOARD: '/staff/dashboard',
    TEACHERS: '/staff/teachers',
    TEACHER_VIEW: '/staff/teachers/:teacherId',
    STUDENTS: '/staff/students',
    STUDENT_VIEW: '/staff/students/:studentId',
    CLASSES: '/staff/classes',
    CLASS_DETAIL: '/staff/classes/:classId',
    EXAMS: '/staff/exams',
    ANALYTICS: '/staff/analytics',
    SETTINGS: '/staff/settings',
  },
  
  // Parent routes
  PARENT: {
    DASHBOARD: '/parent/dashboard',
    CHILDREN: '/parent/children',
    CHILD_RESULTS: '/parent/children/:studentId/results',
    SETTINGS: '/parent/settings',
  },
  
  // Editor routes
  EDITOR: {
    PLAYGROUND: '/playground',
    PLAYGROUND_ENGLISH: '/playground/english',
    PLAYGROUND_CHINESE: '/playground/chinese',
    PLAYGROUND_ESSTOOLSBOX: '/playground/ess-toolsbox',
    PLAYGROUND_LCTOOLSBOX: '/playground/lctoolsbox',
    PLAYGROUND_ECAMPUS: '/playground/ecampus',
    PLAYGROUND_MYCLASS: '/playground/myclass',
    PLAYGROUND_MYSTUDY: '/playground/mystudy',
    /** Learns2gether School Education Hub (Campus group) */
    LEARNS2GETHER_HUB: '/campus/learns2gether-hub',
    AGENT_FLOW_PRINCIPAL_KPI: '/campus/agent-flow/principal-kpi-dashboard',
    AGENT_FLOW_TEACHER_WORKFLOW: '/campus/agent-flow/teacher-workflow',
    AGENT_FLOW_TEACHER_DAILY: '/campus/agent-flow/teacher-daily-school-life',
    AGENT_FLOW_STUDENT_PROFILE: '/campus/agent-flow/student-learning-profile',
    AGENT_FLOW_STUDENT_EXAM: '/campus/agent-flow/student-exam-lifecycle',
    AGENT_FLOW_STUDENT_DAILY: '/campus/agent-flow/student-daily-school-life',
    DASHBOARD: '/editor',
    /** Hub when exiting playground (dashboard, not question bank). */
    DASHBOARD_HUB: '/editor/dashboard',
    TABLE: '/editor/:tableName',
    APPS: '/editor/apps',
    NOTES: '/editor/notes',
    DESIGNER: '/editor/designer',
    SKETCH_EDITOR: '/editor/sketch',
    CALCULATOR_EDITOR: '/editor/calculator',
    MATHSAITOOLS_SKETCHUP: '/editor/sketchup',
    SKETCHUP_CANVAS_API_LISTINGS: '/editor/sketchup/api-listings',
    SKETCHUP_ANIMATE: '/editor/sketchup-animate',
    SKETCHUP_ANIMATE_PREVIEW: '/editor/sketchup-animate/preview',
    SKETCHUP_ANIMATE_PARAMS_DEMO: '/editor/sketchup-animate/params-demo',
    SKETCHUP_ANIMATE_TOOLBOX_LISTINGS: '/editor/sketchup-animate/toolbox-listings',
    SKETCHUP_ANIMATE_HKDSE_2019_Q17: '/editor/sketchup-animate/hkdse/2019-q17',
    SKETCHUP_ANIMATE_HKDSE_2019_Q18: '/editor/sketchup-animate/hkdse/2019-q18',
    SKETCHUP_ANIMATE_HKDSE_2019_Q19: '/editor/sketchup-animate/hkdse/2019-q19',
    SKETCHUP_ANIMATE_HKDSE_2019_Q13: '/editor/sketchup-animate/hkdse/2019-q13',
    SKETCHUP_ANIMATE_HKDSE_2019_Q14: '/editor/sketchup-animate/hkdse/2019-q14',
    SKETCHUP_ANIMATE_HKDSE_2018_Q13: '/editor/sketchup-animate/hkdse/2018-q13',
    SKETCHUP_ANIMATE_HKDSE_2018_Q14: '/editor/sketchup-animate/hkdse/2018-q14',
    SKETCHUP_ANIMATE_HKDSE_2018_Q17: '/editor/sketchup-animate/hkdse/2018-q17',
    SKETCHUP_ANIMATE_HKDSE_2018_Q18: '/editor/sketchup-animate/hkdse/2018-q18',
    SKETCHUP_ANIMATE_HKDSE_2018_Q19: '/editor/sketchup-animate/hkdse/2018-q19',
    SKETCHUP_ANIMATE_HKDSE_2017_Q10: '/editor/sketchup-animate/hkdse/2017-q10',
    SKETCHUP_ANIMATE_HKDSE_2017_Q11: '/editor/sketchup-animate/hkdse/2017-q11',
    SKETCHUP_ANIMATE_HKDSE_2017_Q12: '/editor/sketchup-animate/hkdse/2017-q12',
    SKETCHUP_ANIMATE_HKDSE_2017_Q13: '/editor/sketchup-animate/hkdse/2017-q13',
    SKETCHUP_ANIMATE_HKDSE_2017_Q18: '/editor/sketchup-animate/hkdse/2017-q18',
    SKETCHUP_ANIMATE_HKDSE_2017_Q19: '/editor/sketchup-animate/hkdse/2017-q19',
    SKETCHUP_ANIMATE_HKDSE_2015_Q08: '/editor/sketchup-animate/hkdse/2015-q08',
    SKETCHUP_ANIMATE_HKDSE_2015_Q09: '/editor/sketchup-animate/hkdse/2015-q09',
    SKETCHUP_ANIMATE_HKDSE_2015_Q10: '/editor/sketchup-animate/hkdse/2015-q10',
    SKETCHUP_ANIMATE_HKDSE_2015_Q11: '/editor/sketchup-animate/hkdse/2015-q11',
    SKETCHUP_ANIMATE_HKDSE_2015_Q12: '/editor/sketchup-animate/hkdse/2015-q12',
    SKETCHUP_ANIMATE_HKDSE_2015_Q13: '/editor/sketchup-animate/hkdse/2015-q13',
    SKETCHUP_ANIMATE_HKDSE_2015_Q14: '/editor/sketchup-animate/hkdse/2015-q14',
    SKETCHUP_ANIMATE_HKDSE_2015_Q15: '/editor/sketchup-animate/hkdse/2015-q15',
    SKETCHUP_ANIMATE_HKDSE_2015_Q16: '/editor/sketchup-animate/hkdse/2015-q16',
    SKETCHUP_ANIMATE_HKDSE_2015_Q17: '/editor/sketchup-animate/hkdse/2015-q17',
    SKETCHUP_ANIMATE_HKDSE_2015_Q18: '/editor/sketchup-animate/hkdse/2015-q18',
    SKETCHUP_ANIMATE_HKDSE_2015_Q19: '/editor/sketchup-animate/hkdse/2015-q19',
    SKETCHUP_ANIMATE_HKDSE_2016_Q07: '/editor/sketchup-animate/hkdse/2016-q07',
    SKETCHUP_ANIMATE_HKDSE_2016_Q10: '/editor/sketchup-animate/hkdse/2016-q10',
    SKETCHUP_ANIMATE_HKDSE_2016_Q11: '/editor/sketchup-animate/hkdse/2016-q11',
    SKETCHUP_ANIMATE_HKDSE_2016_Q12: '/editor/sketchup-animate/hkdse/2016-q12',
    SKETCHUP_ANIMATE_HKDSE_2016_Q13: '/editor/sketchup-animate/hkdse/2016-q13',
    SKETCHUP_ANIMATE_HKDSE_2016_Q15: '/editor/sketchup-animate/hkdse/2016-q15',
    SKETCHUP_ANIMATE_HKDSE_2016_Q17: '/editor/sketchup-animate/hkdse/2016-q17',
    SKETCHUP_ANIMATE_HKDSE_2016_Q18: '/editor/sketchup-animate/hkdse/2016-q18',
    SKETCHUP_ANIMATE_HKDSE_2016_Q19: '/editor/sketchup-animate/hkdse/2016-q19',
    SKETCHUP_ANIMATE_HKDSE_2016_Q20: '/editor/sketchup-animate/hkdse/2016-q20',
    SKETCHUP_ANIMATE_HKDSE_2012_Q08: '/editor/sketchup-animate/hkdse/2012-q08',
    SKETCHUP_ANIMATE_HKDSE_2013_Q07: '/editor/sketchup-animate/hkdse/2013-q07',
    SKETCHUP_ANIMATE_HKDSE_2013_Q14: '/editor/sketchup-animate/hkdse/2013-q14',
    SKETCHUP_ANIMATE_HKDSE_2013_Q18: '/editor/sketchup-animate/hkdse/2013-q18',
    SKETCHUP_ANIMATE_HKDSE_2014_Q12: '/editor/sketchup-animate/hkdse/2014-q12',
    SKETCHUP_ANIMATE_HKDSE_2014_Q13: '/editor/sketchup-animate/hkdse/2014-q13',
    SKETCHUP_ANIMATE_HKDSE_2014_Q14: '/editor/sketchup-animate/hkdse/2014-q14',
    SKETCHUP_ANIMATE_HKDSE_2014_Q17: '/editor/sketchup-animate/hkdse/2014-q17',
    TOOLBOX: '/editor/toolbox',
    MY_STUDY: '/editor/my-study',
    TOOLBOX_HKDSE_LISTINGS: '/editor/toolbox/hkdse-listings',
    TOOLBOX_HKDSE_LISTINGS_P2: '/editor/toolbox/hkdse-listings-p2',
    ESSTOOLSBOX_API_FULL_LIST: '/editor/ess-toolsbox-api-full-list',
    IDIOMSTOOLSBOX_API_FULL_LIST: '/editor/idioms-toolsbox-api-full-list',
    VERBSTOOLSBOX_API_FULL_LIST: '/editor/verbs-toolsbox-api-full-list',
    PHRASALVERBTOOLSBOX_API_FULL_LIST: '/editor/phrasalverb-toolsbox-api-full-list',
    LCTOOLSBOX_API_FULL_LIST: '/editor/lctoolsbox-api-full-list',
    CHISTORYTOOLSBOX_API_FULL_LIST: '/editor/chistory-toolsbox-api-full-list',
    PLAYGROUND_CHISTORYTOOLSBOX: '/playground/chistory-toolsbox',
    HISTORYTOOLSBOX_API_FULL_LIST: '/editor/history-toolsbox-api-full-list',
    PLAYGROUND_HISTORYTOOLSBOX: '/playground/history-toolsbox',
    VOCABTOOLSBOX_API_FULL_LIST: '/editor/vocab-toolsbox-api-full-list',
    PLAYGROUND_VOCABTOOLSBOX: '/playground/vocab-toolsbox',
    PLAYGROUND_AIGCTOOLSBOX: '/playground/aigc-toolsbox',
    BAFSTOOLSBOX_API_FULL_LIST: '/editor/bafs-toolsbox-api-full-list',
    PLAYGROUND_BAFSTOOLSBOX: '/playground/bafs-toolsbox',
    ACCOUNTTOOLSBOX_API_FULL_LIST: '/editor/account-toolsbox-api-full-list',
    PLAYGROUND_ACCOUNTTOOLSBOX: '/playground/account-toolsbox',
    ICTTOOLSBOX_API_FULL_LIST: '/editor/ict-toolsbox-api-full-list',
    PLAYGROUND_ICTTOOLSBOX: '/playground/ict-toolsbox',
    BUSINESSTOOLSBOX_API_FULL_LIST: '/editor/business-toolsbox-api-full-list',
    PLAYGROUND_BUSINESSTOOLSBOX: '/playground/business-toolsbox',
    BIOLOGYTOOLSBOX_API_FULL_LIST: '/editor/biology-toolsbox-api-full-list',
    PLAYGROUND_BIOLOGYTOOLSBOX: '/playground/biology-toolsbox',
    TOOLBOX_AGENT: '/editor/toolbox/agent/:agentId',
    TOOLBOX_AGENT_TOOL: '/editor/toolbox/agent/:agentId/tool/:toolId',
    TOOLBOX_AGENT_TOOL_SUBTOOL: '/editor/toolbox/agent/:agentId/tool/:toolId/subtool/:subtoolId',
    /** SketchUpEnhanced ToolBox: M1 & M2 subjects, MathematicsEnhanced Playground */
    TOOLBOX_ENHANCED: '/editor/toolbox-enhanced',
    TOOLBOX_ENHANCED_AGENT: '/editor/toolbox-enhanced/agent/:agentId',
    TOOLBOX_ENHANCED_AGENT_TOOL: '/editor/toolbox-enhanced/agent/:agentId/tool/:toolId',
    TOOLBOX_ENHANCED_AGENT_TOOL_SUBTOOL: '/editor/toolbox-enhanced/agent/:agentId/tool/:toolId/subtool/:subtoolId',
    PLAYGROUND_MATHS_ENHANCED: '/playground/maths-enhanced',
  },
  
  // Error routes
  ERRORS: {
    NOT_FOUND: '/404',
    UNAUTHORIZED: '/401',
    FORBIDDEN: '/403',
    SERVER_ERROR: '/500',
  },
};

/**
 * Helper function to generate dynamic routes
 */
export const generateRoute = (route, params = {}) => {
  let path = route;
  Object.keys(params).forEach((key) => {
    path = path.replace(`:${key}`, params[key]);
  });
  return path;
};

/**
 * Route groups for navigation
 */
export const NAV_GROUPS = {
  STUDENT: [
    { path: ROUTES.STUDENT.DASHBOARD, label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: ROUTES.STUDENT.EXAMS, label: 'My Exams', icon: 'FileText' },
    { path: ROUTES.STUDENT.RESULTS, label: 'Results', icon: 'Award' },
    { path: ROUTES.STUDENT.PROGRESS, label: 'Progress', icon: 'TrendingUp' },
    { path: ROUTES.STUDENT.APPSOKAY_TUTOR, label: 'Tutor', icon: 'BookOpen' },
    { path: ROUTES.STUDENT.SETTINGS, label: 'Settings', icon: 'Settings' },
  ],
  TEACHER: [
    { path: ROUTES.TEACHER.DASHBOARD, label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: ROUTES.TEACHER.EXAMS, label: 'My Exams', icon: 'FileText' },
    { path: ROUTES.TEACHER.QUESTIONS, label: 'Question Bank', icon: 'HelpCircle' },
    { path: ROUTES.TEACHER.CLASSES, label: 'Classes', icon: 'Users' },
    { path: ROUTES.TEACHER.GRADING, label: 'Grading', icon: 'ClipboardCheck' },
    { path: ROUTES.TEACHER.ANALYTICS, label: 'Analytics', icon: 'BarChart3' },
    { path: ROUTES.EDITOR.APPS, label: 'Apps', icon: 'Smartphone' },
    { path: ROUTES.EDITOR.NOTES, label: 'Notes', icon: 'BookOpen' },
    { path: ROUTES.EDITOR.DESIGNER, label: 'Designer', icon: 'Sparkles' },
    { path: ROUTES.TEACHER.SETTINGS, label: 'Settings', icon: 'Settings' },
  ],
  ADMIN: [
    { path: ROUTES.ADMIN.DASHBOARD, label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: ROUTES.ADMIN.SCHOOLS, label: 'Schools', icon: 'Building2' },
    { path: ROUTES.ADMIN.SAAS_DATA, label: 'SaaS Data', icon: 'Database' },
    { path: ROUTES.ADMIN.USERS, label: 'Users', icon: 'Users' },
    { path: ROUTES.ADMIN.EXAMS, label: 'All Exams', icon: 'FileText' },
    { path: ROUTES.EDITOR.APPS, label: 'Apps', icon: 'Smartphone' },
    { path: ROUTES.EDITOR.NOTES, label: 'Notes', icon: 'BookOpen' },
    { path: ROUTES.EDITOR.DESIGNER, label: 'Designer', icon: 'Sparkles' },
    { path: ROUTES.ADMIN.SECURITY, label: 'Security', icon: 'Shield' },
    { path: ROUTES.ADMIN.SETTINGS, label: 'Settings', icon: 'Settings' },
  ],
  STAFF: [
    { path: ROUTES.STAFF.DASHBOARD, label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: ROUTES.STAFF.TEACHERS, label: 'Teachers', icon: 'Users' },
    { path: ROUTES.STAFF.STUDENTS, label: 'Students', icon: 'GraduationCap' },
    { path: ROUTES.STAFF.CLASSES, label: 'Classes', icon: 'BookOpen' },
    { path: ROUTES.STAFF.EXAMS, label: 'Exams', icon: 'FileText' },
    { path: ROUTES.STAFF.ANALYTICS, label: 'Analytics', icon: 'BarChart3' },
    { path: ROUTES.EDITOR.APPS, label: 'Apps', icon: 'Smartphone' },
    { path: ROUTES.EDITOR.NOTES, label: 'Notes', icon: 'BookOpen' },
    { path: ROUTES.EDITOR.DESIGNER, label: 'Designer', icon: 'Sparkles' },
    { path: ROUTES.STAFF.SETTINGS, label: 'Settings', icon: 'Settings' },
  ],
  PARENT: [
    { path: ROUTES.PARENT.DASHBOARD, label: 'Dashboard', icon: 'LayoutDashboard' },
    { path: ROUTES.PARENT.CHILDREN, label: 'My Children', icon: 'Users' },
    { path: ROUTES.PARENT.SETTINGS, label: 'Settings', icon: 'Settings' },
  ],
};

export default ROUTES;
